/**
 * Shared mapping between the different filter param names used across
 * list pages (/cars uses pret_min/an_min/km_max/location, /vehicles uses
 * priceFrom/yearFrom/mileageTo, older code used pretMin/anMin/kmMax/oras)
 * and the saved-search / price-alert form fields.
 */
export type SavedSearchFields = {
  marca?: string;
  model?: string;
  pretMin?: string;
  pretMax?: string;
  anMin?: string;
  anMax?: string;
  kmMax?: string;
  combustibil?: string;
  transmisie?: string;
  locatie?: string;
  tip?: string;
};

export const FILTER_ALIASES: Record<keyof SavedSearchFields, string[]> = {
  marca: ['marca', 'make', 'brand'],
  model: ['model'],
  pretMin: ['pret_min', 'pretMin', 'price_min', 'priceMin', 'priceFrom'],
  pretMax: ['pret_max', 'pretMax', 'price_max', 'priceMax', 'priceTo'],
  anMin: ['an_min', 'anMin', 'year_min', 'yearMin', 'yearFrom'],
  anMax: ['an_max', 'anMax', 'year_max', 'yearMax', 'yearTo'],
  kmMax: ['km_max', 'kmMax', 'mileage_max', 'mileageMax', 'mileageTo'],
  combustibil: ['combustibil', 'fuel', 'fuelType', 'fuel_type'],
  transmisie: ['transmisie', 'transmission'],
  locatie: ['location', 'city', 'oras', 'locatie'],
  tip: ['tip', 'type'],
};

type Source = URLSearchParams | Record<string, unknown> | null | undefined;

const read = (src: Source, key: string): string | undefined => {
  if (!src) return undefined;
  const v = src instanceof URLSearchParams ? src.get(key) : (src as Record<string, unknown>)[key];
  if (v === null || v === undefined) return undefined;
  const s = String(v).trim();
  return s ? s : undefined;
};

export function readSavedSearchFields(src: Source): SavedSearchFields {
  const out: SavedSearchFields = {};
  (Object.keys(FILTER_ALIASES) as (keyof SavedSearchFields)[]).forEach((field) => {
    for (const k of FILTER_ALIASES[field]) {
      const v = read(src, k);
      if (v !== undefined) {
        out[field] = v;
        break;
      }
    }
  });
  return out;
}

export const hasSavedSearchFields = (f: SavedSearchFields) =>
  Object.values(f).some((v) => v !== undefined && v !== '');

/** Map free-form fuel values to the price-alert form options. */
export function alertFuelOption(v?: string): '' | 'benzina' | 'diesel' | 'hibrid' | 'electric' {
  const s = (v || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  if (!s) return '';
  if (s.includes('hibrid') || s.includes('hybrid') || s.includes('plug')) return 'hibrid';
  if (s.includes('diesel') || s.includes('motorina')) return 'diesel';
  if (s.includes('electric')) return 'electric';
  if (s.includes('benzin') || s.includes('petrol') || s.includes('gasoline')) return 'benzina';
  return '';
}

/** Map free-form transmission values to the price-alert form options. */
export function alertTransmissionOption(v?: string): '' | 'manuala' | 'automata' {
  const s = (v || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  if (!s) return '';
  if (s.startsWith('manual')) return 'manuala';
  if (s.startsWith('autom')) return 'automata';
  return '';
}

const toInt = (v?: string) => {
  const n = v ? parseInt(v, 10) : NaN;
  return Number.isFinite(n) ? n : undefined;
};

/** Pre-fill values for the price-alert form from URL search params. */
export function alertPrefillFromSearch(src: Source) {
  const f = readSavedSearchFields(src);
  return {
    has: hasSavedSearchFields({ ...f, tip: undefined }),
    values: {
      make: f.marca || '',
      model: f.model || '',
      year_min: toInt(f.anMin),
      year_max: toInt(f.anMax),
      price_max: toInt(f.pretMax),
      location: f.locatie || '',
      fuel_type: alertFuelOption(f.combustibil),
      transmission: alertTransmissionOption(f.transmisie),
    },
  };
}
